import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Sparkles, FloatingParticles, WaterReflection } from './Sparkles';
export function Hero() {
  return <section className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-pond-dark px-6 pt-24 pb-16" data-id="element-88">
      {/* Stillness: Slow water ripples and floating light motes */}
      <Sparkles count={10} className="z-0 opacity-60" data-id="element-89" />
      <FloatingParticles count={8} data-id="element-90" />

      {/* Subtle water reflection lines */}
      <WaterReflection className="top-1/3" data-id="element-91" />
      <WaterReflection className="top-2/3" data-id="element-92" />

      {/* Soft gradient orbs - like light filtering through water */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-93">
        <motion.div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-pond-blue/8 rounded-full blur-[150px]" animate={{
        opacity: [0.5, 0.7, 0.5]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-94" />
        <motion.div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-pond-lavender/6 rounded-full blur-[120px]" animate={{
        opacity: [0.4, 0.6, 0.4]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-95" />
        {/* Soft mint accent */}
        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[100px]" animate={{
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 4
      }} data-id="element-96" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-97">
        {/* Phone Mockup - Left Side */}
        <motion.div className="flex-1 flex justify-center lg:justify-end" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} data-id="element-98">
          <div className="relative" data-id="element-99">
            {/* Gentle ripples around phone */}
            <Sparkles count={4} className="-inset-24 opacity-40" data-id="element-100" />

            {/* Phone Frame */}
            <div className="relative w-[280px] md:w-[320px] h-[580px] md:h-[660px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[50px] p-2 shadow-2xl" data-id="element-101">
              {/* Screen */}
              <div className="w-full h-full rounded-[42px] overflow-hidden relative bg-black" data-id="element-102">
                <img src="https://cdn.magicpatterns.com/uploads/dk2JwRssW5PaffnbgKaw84/Routine_main.png" alt="Pond App Screenshot" className="w-full h-full object-contain" data-id="element-103" />
              </div>
            </div>

            {/* Soft glow behind phone */}
            <motion.div className="absolute inset-0 bg-pond-blue/15 blur-[100px] -z-10 scale-75" animate={{
            opacity: [0.4, 0.6, 0.4]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-104" />
          </div>
        </motion.div>

        {/* Content - Right Side */}
        <motion.div className="flex-1 text-center lg:text-left relative" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.5,
        ease: 'easeOut',
        delay: 0.3
      }} data-id="element-105">
          {/* App Store Rating */}
          <motion.div className="flex items-center gap-3 justify-center lg:justify-start mb-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} data-id="element-106">
            <div className="flex items-center gap-2 px-4 py-2 bg-pond-dark-card/50 backdrop-blur-sm rounded-full border border-pond-blue/10" data-id="element-107">
              <div className="flex items-center gap-1.5" data-id="element-108">
                <span className="text-pond-cream font-medium" data-id="element-109">4.8</span>
                <div className="flex" data-id="element-110">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < 4 ? 'fill-pond-blue text-pond-blue' : 'fill-pond-blue/50 text-pond-blue/50'} data-id="element-111" />)}
                </div>
              </div>
              <span className="text-pond-cream-muted text-sm" data-id="element-112">
                on the App Store
              </span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif mb-6 leading-[1.1]" data-id="element-113">
            <span className="gradient-text" data-id="element-114">Less noise. More you.</span>
          </h1>

          <p className="text-lg md:text-xl text-pond-cream-muted font-light mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed" data-id="element-115">
            Pond gently blocks distractions so you can be present for the
            moments that matter.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-116">
            <motion.a href="https://go.pixo.co/pond_landing" target="_blank" rel="noopener noreferrer" className="group relative inline-block px-8 py-4 bg-gradient-to-r from-pond-blue to-pond-lavender text-pond-dark rounded-full font-medium text-lg overflow-hidden transition-all hover:shadow-xl hover:shadow-pond-blue/20 text-center" whileHover={{
            y: -2
          }} whileTap={{
            scale: 0.98
          }} data-id="element-117">
              <span className="relative z-10" data-id="element-118">Download App</span>
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20" animate={{
      y: [0, 6, 0]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-119">
        <div className="w-6 h-10 rounded-full border border-pond-blue/20 flex justify-center pt-2" data-id="element-120">
          <motion.div className="w-1 h-2 bg-pond-blue/40 rounded-full" animate={{
          opacity: [0.3, 0.7, 0.3]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-121" />
        </div>
      </motion.div>
    </section>;
}